import { FormEvent, useState } from 'react';
import {
  CheckCircle2,
  Fingerprint,
  Loader2,
  Lock,
  LogIn,
  Mail,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { loginWithPasskey, webauthnSupported } from '../api/webauthn';

type Mode = 'magic' | 'password';

export default function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const requestLink = useAuthStore((s) => s.requestLink);
  const setSession = useAuthStore((s) => s.setSession);
  const resetFlow = useAuthStore((s) => s.resetFlow);
  const loginError = useAuthStore((s) => s.loginError);
  const linkRequested = useAuthStore((s) => s.linkRequested);
  const requesting = useAuthStore((s) => s.requesting);
  const loggingIn = useAuthStore((s) => s.loggingIn);

  // Par défaut : connexion e-mail + mot de passe (le lien magique reste
  // accessible via la bascule « Recevoir un lien par e-mail »).
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [bioBusy, setBioBusy] = useState(false);
  const [bioError, setBioError] = useState<string | null>(null);

  const switchMode = (next: Mode) => {
    resetFlow();
    setBioError(null);
    setMode(next);
  };

  const onSubmitMagic = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    void requestLink(email.trim());
  };

  const onSubmitPassword = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    void login({ email: email.trim(), password });
  };

  const onBiometric = async () => {
    setBioBusy(true);
    setBioError(null);
    try {
      const session = await loginWithPasskey();
      setSession(session);
    } catch {
      setBioError(
        'Connexion biométrique impossible. Utilisez votre lien e-mail ou votre mot de passe, puis activez FaceID depuis votre espace.',
      );
    } finally {
      setBioBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-permio.png" alt="Permio" className="h-9 w-auto mb-4" />
          <h1 className="text-slate-900 text-center text-lg font-semibold">
            Mon espace moniteur
          </h1>
          <p className="text-sm text-slate-500 mt-1 text-center">
            {mode === 'magic'
              ? 'Recevez un lien de connexion par e-mail.'
              : 'Connectez-vous avec votre mot de passe.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          {/* Confirmation d'envoi du lien magique */}
          {mode === 'magic' && linkRequested ? (
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 size={26} />
              </div>
              <p className="text-sm text-slate-700">
                Si un compte moniteur correspond à <b>{email}</b>, un lien de
                connexion vient d'être envoyé. Vérifiez votre boîte mail.
              </p>
              <button
                onClick={() => resetFlow()}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
              >
                Renvoyer un lien
              </button>
            </div>
          ) : mode === 'magic' ? (
            <form onSubmit={onSubmitMagic} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Adresse e-mail
                </label>
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
                    placeholder="vous@auto-ecole.fr"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={requesting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
              >
                {requesting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Mail size={16} />
                )}
                {requesting ? 'Envoi…' : 'Recevoir mon lien de connexion'}
              </button>
            </form>
          ) : (
            <form onSubmit={onSubmitPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Adresse e-mail
                </label>
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
                    placeholder="vous@auto-ecole.fr"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loggingIn}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
              >
                {loggingIn ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <LogIn size={16} />
                )}
                {loggingIn ? 'Connexion…' : 'Se connecter'}
              </button>
            </form>
          )}

          {(loginError || bioError) && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {loginError ?? bioError}
            </div>
          )}

          {/* Connexion biométrique */}
          {webauthnSupported() && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">ou</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
              <button
                onClick={onBiometric}
                disabled={bioBusy}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-sm font-semibold rounded-lg transition-colors"
              >
                {bioBusy ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Fingerprint size={16} className="text-indigo-600" />
                )}
                {bioBusy ? 'Connexion…' : 'Connexion biométrique (FaceID)'}
              </button>
            </>
          )}
        </div>

        {/* Bascule entre les modes */}
        <div className="text-center mt-5">
          {mode === 'magic' ? (
            <button
              onClick={() => switchMode('password')}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              J'ai déjà un mot de passe
            </button>
          ) : (
            <button
              onClick={() => switchMode('magic')}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Recevoir un lien par e-mail
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
